import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ShieldAlert, Lock, Eye, Check, X, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

// Organizer V1 — Phase 1 admin moderation. Operates on the organizer_applications
// lifecycle via /admin/organizer-applications* (kept separate from the pre-existing
// /admin/organizers profile endpoints). Actions: Approve, Reject (optional reason),
// Request Changes (optional message). Approve grants the organizer role + creates the
// public profile server-side. Rejected/request_changes applicants can edit & resubmit.

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'request_changes', label: 'Changes Requested' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'suspended', label: 'Suspended' },
];

const STATUS_BADGE = {
  pending: { cls: 'border-amber-500 text-amber-600', label: 'Pending' },
  approved: { cls: 'bg-green-600', label: 'Approved' },
  request_changes: { cls: 'border-blue-500 text-blue-600', label: 'Changes Requested' },
  rejected: { cls: 'border-red-500 text-red-600', label: 'Rejected' },
  suspended: { cls: 'border-muted-foreground text-muted-foreground', label: 'Suspended' },
};

function Centered({ children }) {
  return <div className="max-w-4xl mx-auto px-4 py-16 text-center opacity-70">{children}</div>;
}
function GateLogin() {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <p className="mb-4">Please log in.</p>
      <Link to="/login"><Button>Go to Login</Button></Link>
    </div>
  );
}
function GateAdmin({ role }) {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <h1 className="text-xl font-semibold mb-2">Admin access required</h1>
      <p className="opacity-80">Your role is <span className="capitalize font-medium">{role || 'unknown'}</span>.</p>
    </div>
  );
}
function StatusBadge({ status }) {
  const b = STATUS_BADGE[status] || { cls: 'border-border', label: status || 'unknown' };
  return <Badge variant={b.cls.startsWith('bg-') ? 'default' : 'outline'} className={b.cls}>{b.label}</Badge>;
}

// Logo preview via authed blob fetch (pending/rejected logos are gated server-side).
function LogoImg({ url }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) return;
    let obj = null, active = true;
    api.fetchAuthedBlobUrl(url).then((u) => { if (active) { obj = u; setSrc(u); } }).catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  if (!url) return null;
  return src ? <img src={src} alt="logo" className="h-20 w-20 rounded-md object-cover border border-border" /> : <div className="h-20 w-20 rounded-md bg-muted animate-pulse" />;
}

export default function AdminOrganizers() {
  const [authed] = useState(api.isAuthenticated());
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchApps = useCallback(async () => {
    try {
      const res = await api.get(`/admin/organizer-applications?status=${tab}`);
      setRows(Array.isArray(res) ? res : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load organizer applications');
      setRows([]);
    }
  }, [tab]);

  useEffect(() => {
    if (!authed) { setLoading(false); return; }
    api.get('/users/me').then((me) => setRole(me.role)).catch(() => {}).finally(() => setLoading(false));
  }, [authed]);

  useEffect(() => {
    if (role === 'admin') fetchApps();
  }, [role, fetchApps]);

  const viewDetail = async (id) => {
    try { setSelected(await api.get(`/admin/organizer-applications/${id}`)); }
    catch (e) { toast.error(e.message || 'Failed to load detail'); }
  };

  const act = async (id, action) => {
    let body;
    if (action === 'reject') {
      const reason = window.prompt('Optional reason (shown to the applicant). Leave blank to skip:');
      if (reason === null) return;   // cancelled
      body = { reason: reason.trim() || null };
    } else if (action === 'request-changes') {
      const message = window.prompt('Optional message for the applicant. Leave blank to skip:');
      if (message === null) return;
      body = { message: message.trim() || null };
    } else if (action === 'approve') {
      if (!window.confirm('Approve this organizer? This grants the organizer role and publishes their profile.')) return;
      body = {};
    }
    setBusy(true);
    try {
      await api.post(`/admin/organizer-applications/${id}/${action}`, body);
      toast.success(`Application ${action === 'request-changes' ? 'sent back for changes' : action + 'd'}.`);
      await fetchApps();
      if (selected?.id === id) await viewDetail(id);
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally { setBusy(false); }
  };

  if (!authed) return <GateLogin />;
  if (loading) return <Centered>Loading…</Centered>;
  if (role !== 'admin') return <GateAdmin role={role} />;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-4"><Building2 className="h-6 w-6" /> Admin · Organizer Applications</h1>

      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map((t) => (
          <Button key={t.key} variant={tab === t.key ? 'default' : 'outline'} size="sm"
            onClick={() => { setTab(t.key); setSelected(null); }}>{t.label}</Button>
        ))}
      </div>

      {selected && (
        <Card className="mb-4 border-primary/40">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">Organizer application detail <StatusBadge status={selected.status} /></CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Close</Button>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="flex gap-4">
              <LogoImg url={selected.logo_url} />
              <div className="space-y-1">
                <div className="text-base"><b>{selected.organization_name || '(unnamed)'}</b></div>
                <div className="opacity-80">{selected.description || 'No description'}</div>
                <div>Contact: {selected.real_name} · {selected.contact_email} · {selected.phone}</div>
                <div>City: {selected.city || '—'}</div>
                {selected.instagram && <div>Instagram: <a className="text-primary underline" href={selected.instagram} target="_blank" rel="noreferrer">{selected.instagram}</a></div>}
                {selected.website && <div>Website: <a className="text-primary underline" href={selected.website} target="_blank" rel="noreferrer">{selected.website}</a></div>}
                <div className="opacity-60">account: {selected.account_email} · slug: {selected.slug || '—'}</div>
                {selected.rejection_reason && <div className="text-red-600">Rejection reason: {selected.rejection_reason}</div>}
                {selected.request_changes_message && <div className="text-blue-600">Changes requested: {selected.request_changes_message}</div>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {['pending', 'request_changes', 'rejected'].includes(selected.status) && (
                <Button size="sm" disabled={busy} onClick={() => act(selected.id, 'approve')}><Check className="h-4 w-4 mr-1" /> Approve</Button>
              )}
              {['pending', 'request_changes'].includes(selected.status) && (
                <>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => act(selected.id, 'request-changes')}><RefreshCw className="h-4 w-4 mr-1" /> Request Changes</Button>
                  <Button size="sm" variant="destructive" disabled={busy} onClick={() => act(selected.id, 'reject')}><X className="h-4 w-4 mr-1" /> Reject</Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {rows.length === 0 && <p className="opacity-60 text-center py-8">No {STATUS_BADGE[tab]?.label?.toLowerCase() || tab} applications.</p>}
        {rows.map((o) => (
          <Card key={o.id}>
            <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate flex items-center gap-2">
                  {o.organization_name || '(unnamed)'} <StatusBadge status={o.status} />
                </div>
                <div className="text-sm opacity-70 truncate">{o.city || '—'} · {o.account_email || o.user_id}</div>
              </div>
              <div className="flex items-center gap-2">
                {['pending', 'request_changes', 'rejected'].includes(o.status) && (
                  <Button size="sm" disabled={busy} onClick={() => act(o.id, 'approve')}>Approve</Button>
                )}
                {['pending', 'request_changes'].includes(o.status) && (
                  <>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => act(o.id, 'request-changes')}>Changes</Button>
                    <Button size="sm" variant="destructive" disabled={busy} onClick={() => act(o.id, 'reject')}>Reject</Button>
                  </>
                )}
                <Button variant="outline" size="sm" onClick={() => viewDetail(o.id)}><Eye className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
